import { ExpectationPromptBox } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/ExpectationPrompt/ExpectationPromptBox"
import { ManualExpectationEditor } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/ManualExpectationEditor.tsx"
import { ConfigProvider, FormInstance, Segmented } from "antd"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"
import { SelectedExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/types.ts"
import {
  AnomalyDetectionCoverageStats,
  ExpectationEditor_DataAssetMetricRunFragmentDoc,
} from "src/api/graphql/graphql.ts"
import { ApolloError } from "@apollo/client"
import {
  AutomaticExpectationEditor,
  isAnomalyDetectionQualityIssue,
} from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/AutomaticExpectationEditor/AutomaticExpectationEditor"
import { theme } from "src/global/ui/themes/theme.ts"
import { styled } from "styled-components"
import { Image } from "src/global/ui/Image"
import { Icon } from "src/global/ui/Icon"
import { FragmentType } from "src/api/graphql"
import {
  SEGMENT_VALUES,
  SegmentValue,
} from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/SimpleNewExpectationDrawer"

const StyledResultContainer = styled.div`
  padding-top: ${theme.spacing.s};
  padding-bottom: ${theme.spacing.m};
`

const StyledIconContainer = styled.span`
  display: inline-block;
  svg {
    width: ${theme.spacing.xs};
    height: ${theme.spacing.xs};
    vertical-align: text-top;
    margin-top: 2px;
  }
  .ant-segmented-item-selected & svg path {
    fill: ${theme.colors.neutralColorPalette.whites.white};
  }
`

type SegmentIconProps = {
  type: "sparkles" | "code" | "sliders" | "bolt"
  ariaLabel: string
}

const SegmentIcon = ({ type, ariaLabel }: SegmentIconProps) => {
  return (
    <StyledIconContainer>
      {type === "sliders" || type === "bolt" ? (
        <Icon name={type} aria-label={ariaLabel} />
      ) : (
        <Image type={type} aria-label={ariaLabel} />
      )}
    </StyledIconContainer>
  )
}

type SegmentContentProps = {
  currentSegment: SegmentValue
  selectedDataQualityIssue: DataQualityIssue | undefined
  anomalyDetectionCoverageStats: AnomalyDetectionCoverageStats | null | undefined
  addAutomaticExpectationToAssetError: ApolloError | undefined
  form: FormInstance
  onSelectExpectation: (expectation: SelectedExpectation) => void
  selectedExpectation: SelectedExpectation
  expectationConfiguration: Record<string, unknown>
  handleConfigChange: (newConfig: Record<string, unknown>) => void
  dataAsset: FragmentType<typeof ExpectationEditor_DataAssetMetricRunFragmentDoc> | undefined
  addExpectationToAssetError: ApolloError | undefined
  promptText: string
  setPromptText: (text: string) => void
  expectAiError: string | null
  isGeneratingExpectation: boolean
}

function SegmentContent({
  currentSegment,
  selectedDataQualityIssue,
  anomalyDetectionCoverageStats,
  addAutomaticExpectationToAssetError,
  form,
  onSelectExpectation,
  selectedExpectation,
  expectationConfiguration,
  handleConfigChange,
  dataAsset,
  addExpectationToAssetError,
  promptText,
  setPromptText,
  expectAiError,
  isGeneratingExpectation,
}: SegmentContentProps) {
  if (currentSegment === SEGMENT_VALUES.AUTOMATIC && selectedDataQualityIssue && anomalyDetectionCoverageStats) {
    return (
      <AutomaticExpectationEditor
        issueType={selectedDataQualityIssue}
        anomalyDetectionCoverageStats={anomalyDetectionCoverageStats}
        addAutomaticExpectationToAssetError={addAutomaticExpectationToAssetError}
      />
    )
  }

  if (currentSegment === SEGMENT_VALUES.GENERATE_SQL) {
    return (
      <ExpectationPromptBox
        form={form}
        promptText={promptText}
        setPromptText={setPromptText}
        expectAiError={expectAiError}
        isGeneratingExpectation={isGeneratingExpectation}
      />
    )
  }

  return (
    <>
      {currentSegment === SEGMENT_VALUES.MANUAL && (
        <StyledResultContainer>
          <div>Set your own rules for what &#34;bad data&#34; looks like:</div>
        </StyledResultContainer>
      )}
      <ManualExpectationEditor
        form={form}
        selectedDataQualityIssue={selectedDataQualityIssue}
        onSelectExpectation={onSelectExpectation}
        selectedExpectation={selectedExpectation}
        expectationConfiguration={expectationConfiguration}
        handleConfigChange={handleConfigChange}
        dataAsset={dataAsset}
        addExpectationToAssetError={addExpectationToAssetError}
      />
    </>
  )
}

type Props = {
  form: FormInstance
  selectedDataQualityIssue: DataQualityIssue | undefined
  onSelectExpectation: (expectation: SelectedExpectation) => void
  selectedExpectation: SelectedExpectation
  expectationConfiguration: Record<string, unknown>
  handleConfigChange: (newConfig: Record<string, unknown>) => void
  dataAsset: FragmentType<typeof ExpectationEditor_DataAssetMetricRunFragmentDoc> | undefined
  addExpectationToAssetError: ApolloError | undefined
  addAutomaticExpectationToAssetError?: ApolloError
  anomalyDetectionCoverageStats?: AnomalyDetectionCoverageStats | null
  activeSegment: SegmentValue
  setUserSelectedSegment: (segment: SegmentValue) => void
  promptText: string
  setPromptText: (text: string) => void
  isGeneratingExpectation: boolean
  expectAiError: string | null
  isExpectAiEligible: boolean
  generateSqlDraftConfigEnabled: boolean
}

export function ExpectationDrawerEditor({
  form,
  selectedDataQualityIssue,
  onSelectExpectation,
  selectedExpectation,
  expectationConfiguration,
  handleConfigChange,
  dataAsset,
  addExpectationToAssetError,
  addAutomaticExpectationToAssetError,
  anomalyDetectionCoverageStats,
  activeSegment,
  setUserSelectedSegment,
  promptText,
  setPromptText,
  isGeneratingExpectation,
  expectAiError,
  isExpectAiEligible,
  generateSqlDraftConfigEnabled,
}: Props) {
  const automaticSegment = {
    value: SEGMENT_VALUES.AUTOMATIC,
    label: "Automatic",
    icon: <SegmentIcon type="bolt" ariaLabel="Automatic" />,
  }

  const manualSegment = {
    value: SEGMENT_VALUES.MANUAL,
    label: "Manual",
    icon: <SegmentIcon type="sliders" ariaLabel="Manual" />,
  }

  const generateSqlSegment = {
    value: SEGMENT_VALUES.GENERATE_SQL,
    label: "Generate SQL",
    icon: <SegmentIcon type="sparkles" ariaLabel="Generate SQL" />,
  }

  const writeOwnSqlSegment = {
    value: SEGMENT_VALUES.WRITE_OWN_SQL,
    label: "Write your own SQL",
    icon: <SegmentIcon type="code" ariaLabel="Write your own SQL" />,
    disabled: isGeneratingExpectation,
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            itemSelectedBg: theme.colors.neutralColorPalette.blacks.colorText,
            itemSelectedColor: theme.colors.neutralColorPalette.whites.white,
            itemColor: theme.colors.neutralColorPalette.blacks.colorText,
            controlItemBgActiveDisabled: theme.colors.neutralColorPalette.blacks.colorTextQuaternary,
            fontSize: parseInt(theme.typography.fontSize.medium),
            controlHeight: 40,
          },
        },
      }}
    >
      {(() => {
        if (isAnomalyDetectionQualityIssue(selectedDataQualityIssue) && anomalyDetectionCoverageStats) {
          return (
            <>
              <Segmented
                options={[automaticSegment, manualSegment]}
                onChange={(e) => setUserSelectedSegment(e as SegmentValue)}
                data-testid="automatic-segmented"
              />
              <SegmentContent
                currentSegment={activeSegment}
                selectedDataQualityIssue={selectedDataQualityIssue}
                anomalyDetectionCoverageStats={anomalyDetectionCoverageStats}
                addAutomaticExpectationToAssetError={addAutomaticExpectationToAssetError}
                form={form}
                onSelectExpectation={onSelectExpectation}
                selectedExpectation={selectedExpectation}
                expectationConfiguration={expectationConfiguration}
                handleConfigChange={handleConfigChange}
                dataAsset={dataAsset}
                addExpectationToAssetError={addExpectationToAssetError}
                promptText={promptText}
                setPromptText={setPromptText}
                expectAiError={expectAiError}
                isGeneratingExpectation={isGeneratingExpectation}
              />
            </>
          )
        }

        if (isExpectAiEligible) {
          return (
            <>
              {!generateSqlDraftConfigEnabled && (
                <Segmented
                  options={[generateSqlSegment, writeOwnSqlSegment]}
                  value={activeSegment}
                  onChange={(e) => setUserSelectedSegment(e as SegmentValue)}
                  data-testid="expect-ai-segmented"
                />
              )}
              <StyledResultContainer>
                <SegmentContent
                  currentSegment={activeSegment}
                  selectedDataQualityIssue={selectedDataQualityIssue}
                  anomalyDetectionCoverageStats={anomalyDetectionCoverageStats}
                  addAutomaticExpectationToAssetError={addAutomaticExpectationToAssetError}
                  form={form}
                  onSelectExpectation={onSelectExpectation}
                  selectedExpectation={selectedExpectation}
                  expectationConfiguration={expectationConfiguration}
                  handleConfigChange={handleConfigChange}
                  dataAsset={dataAsset}
                  addExpectationToAssetError={addExpectationToAssetError}
                  promptText={promptText}
                  setPromptText={setPromptText}
                  expectAiError={expectAiError}
                  isGeneratingExpectation={isGeneratingExpectation}
                />
              </StyledResultContainer>
            </>
          )
        }

        return (
          <ManualExpectationEditor
            form={form}
            selectedDataQualityIssue={selectedDataQualityIssue}
            onSelectExpectation={onSelectExpectation}
            selectedExpectation={selectedExpectation}
            expectationConfiguration={expectationConfiguration}
            handleConfigChange={handleConfigChange}
            dataAsset={dataAsset}
            addExpectationToAssetError={addExpectationToAssetError}
          />
        )
      })()}
    </ConfigProvider>
  )
}
